import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, ArrowLeft, ArrowRight, Award, Info, CheckCircle2, TrendingUp,
  Rocket, CalendarCheck, Layers, Share2, Link2, Mail, MessageCircle, Linkedin, Quote,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import ShareDialog from "@/components/ShareDialog";
import { fetchHandlungsfeld } from "@/lib/apiClient";

const ZOHO_BOOKING = "https://fe-fenance.zohobookings.eu/#/239609000000041076";

const noteColor = (n) => (n <= 2 ? "#22c55e" : n <= 4 ? "#f18e00" : "#ef4444");
const zoneKeyForNote = (n) => (n <= 2 ? "3" : n <= 4 ? "2" : "1");

const ShareBar = ({ hf }) => {
  const [liOpen, setLiOpen] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = `${hf.name} – feNANCE Tragfähigkeits-Check`;
  const text = `Playbook „${hf.name}": ${hf.tagline}`;
  const enc = encodeURIComponent;

  const linkedInPost =
    `Worüber im Mittelstand zu selten offen gesprochen wird: ${hf.name}.\n\n` +
    (hf.zitat?.text ? `„${hf.zitat.text}" – ${hf.zitat.author}\n\n` : "") +
    `${hf.tagline}\n\n` +
    `Dieses Playbook aus dem feNANCE Tragfähigkeits-Check zeigt konkret, wie man in diesem ` +
    `Handlungsfeld vom Blindflug zum Instrumentenflug kommt – und damit echten Unternehmenswert schafft.\n\n` +
    `#Mittelstand #CFO #Finanzen #Unternehmenswert #feNANCE`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link kopiert – teile das Playbook mit einem Kollegen.");
    } catch {
      toast.error("Kopieren nicht möglich.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (e) { console.debug("Teilen abgebrochen:", e?.message); }
    } else {
      copyLink();
    }
  };

  const links = [
    { id: "mail", icon: Mail, label: "E-Mail",
      href: `mailto:?subject=${enc(title)}&body=${enc(text + "\n\n" + url)}` },
    { id: "whatsapp", icon: MessageCircle, label: "WhatsApp",
      href: `https://wa.me/?text=${enc(text + " " + url)}` },
  ];

  return (
    <div className="bg-card border border-white/10 p-6" data-testid="hf-share-bar">
      <div className="flex items-center gap-2 mb-4">
        <Share2 className="h-4 w-4 text-brand-orange" />
        <span className="font-display text-xs uppercase tracking-wide text-white">
          Playbook teilen
        </span>
      </div>
      <p className="text-xs text-white/50 leading-relaxed mb-4">
        Senden Sie dieses Handlungsfeld an eine Kollegin oder Ihren Steuerberater.
      </p>
      <button
        onClick={() => setLiOpen(true)}
        data-testid="hf-share-linkedin"
        className="w-full flex items-center justify-center gap-2 h-11 mb-2 bg-brand-orange text-black hover:bg-white transition-colors text-xs uppercase tracking-wider font-bold"
      >
        <Linkedin className="h-4 w-4" /> Als LinkedIn-Post teilen
      </button>
      <div className="grid grid-cols-3 gap-2">
        <button
          onClick={copyLink}
          data-testid="hf-share-copy"
          className="flex items-center justify-center gap-2 h-11 border border-white/15 text-white/80 hover:border-brand-orange hover:text-brand-orange transition-colors text-xs uppercase tracking-wider"
        >
          <Link2 className="h-4 w-4" /> Link
        </button>
        {links.map((l) => (
          <a
            key={l.id}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`hf-share-${l.id}`}
            className="flex items-center justify-center gap-2 h-11 border border-white/15 text-white/80 hover:border-brand-orange hover:text-brand-orange transition-colors text-xs uppercase tracking-wider"
          >
            <l.icon className="h-4 w-4" /> {l.label}
          </a>
        ))}
      </div>
      <button
        onClick={nativeShare}
        data-testid="hf-share-native"
        className="mt-2 w-full flex items-center justify-center gap-2 h-11 bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 transition-colors text-xs uppercase tracking-wider"
      >
        <Share2 className="h-4 w-4" /> Mehr Optionen
      </button>
      <ShareDialog open={liOpen} onOpenChange={setLiOpen} url={url} title={title} defaultText={linkedInPost} />
    </div>
  );
};

export default function HandlungsfeldDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchHandlungsfeld(id)
      .then(setData)
      .catch(() => toast.error("Handlungsfeld nicht gefunden."))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-white/60">Handlungsfeld nicht gefunden.</p>
        <Link to="/" className="text-brand-orange underline">Zur Startseite</Link>
      </div>
    );
  }

  const hf = data.handlungsfeld;
  const zl = hf.zone_labels;
  const numId = parseInt(id, 10);
  const prevId = numId > 1 ? numId - 1 : null;
  const nextId = numId < data.total ? numId + 1 : null;

  return (
    <div className="min-h-screen relative z-10" data-testid="hf-detail-page">
      <BrandHeader />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-14">
          <Link
            to="/"
            data-testid="hf-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/50 hover:text-brand-orange transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Alle Handlungsfelder
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 flex items-start gap-5"
          >
            <span className="font-display text-brand-orange text-5xl sm:text-7xl leading-none">
              {String(hf.id).padStart(2, "0")}
            </span>
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
                Handlungsfeld · Tragfähigkeits-Check Playbook
              </span>
              <h1 className="mt-2 font-display uppercase text-3xl sm:text-5xl tracking-tight text-white leading-tight">
                {hf.name}
              </h1>
              <p className="mt-3 text-brand-orange/90 italic text-base">"{hf.tagline}"</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ZITAT — klarer Einstieg */}
      {hf.zitat && (
        <section className="border-b border-white/10 bg-gradient-to-br from-brand-orange/10 to-transparent">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
            <motion.figure
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative pl-10 sm:pl-14"
              data-testid="hf-zitat"
            >
              <Quote className="absolute top-0 left-0 h-9 w-9 text-brand-orange/50" aria-hidden />
              <blockquote>
                <p className="font-display text-xl sm:text-3xl leading-snug text-white">
                  „{hf.zitat.text}"
                </p>
                <figcaption className="mt-4 text-sm uppercase tracking-[0.15em] text-brand-orange">
                  — {hf.zitat.author}
                </figcaption>
              </blockquote>
              {hf.zitat.bezug && (
                <p className="mt-6 text-sm sm:text-base text-white/70 leading-relaxed max-w-3xl">
                  {hf.zitat.bezug}
                </p>
              )}
            </motion.figure>
          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 grid lg:grid-cols-3 gap-8">
        {/* LEFT: narrative */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-brand-orange" />
              <h2 className="font-display text-sm uppercase tracking-wide text-white">
                Warum dieses Feld über Wert entscheidet
              </h2>
            </div>
            <p className="text-sm text-white/75 leading-relaxed">{hf.warum}</p>
            <p className="mt-4 text-sm text-white/55 leading-relaxed border-l-2 border-white/10 pl-4">
              {hf.inhalt}
            </p>
          </section>

          {/* Maturity ladder */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Layers className="h-4 w-4 text-brand-orange" />
              <h2 className="font-display text-sm uppercase tracking-wide text-white">
                Die Reifegrad-Leiter · von Blindflug zu Instrumentenflug
              </h2>
            </div>
            <div className="space-y-2" data-testid="hf-ladder">
              {[6, 5, 4, 3, 2, 1].map((note) => {
                const c = noteColor(note);
                return (
                  <div
                    key={note}
                    className="flex items-stretch border border-white/10 bg-card overflow-hidden"
                  >
                    <div
                      className="w-14 shrink-0 flex flex-col items-center justify-center gap-1"
                      style={{ backgroundColor: `${c}1a` }}
                    >
                      <span
                        className="h-8 w-8 flex items-center justify-center font-display text-sm"
                        style={{ backgroundColor: c, color: "#000" }}
                      >
                        {note}
                      </span>
                    </div>
                    <div className="p-4">
                      <span
                        className="text-[0.6rem] uppercase tracking-[0.15em] font-semibold"
                        style={{ color: c }}
                      >
                        {zl[zoneKeyForNote(note)]}
                      </span>
                      <p className="mt-1 text-sm text-white/75 leading-relaxed">
                        {hf.grades[note]}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Best practices */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-4 w-4 text-brand-orange" />
              <h2 className="font-display text-sm uppercase tracking-wide text-white">
                Best Practice: So sieht Spitzensteuerung aus
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {hf.best_practices.map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-orange shrink-0 mt-0.5" />
                  <span className="text-sm text-white/70 leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
            {hf.beispiel && (
              <div className="mt-6 bg-card border-l-2 border-brand-orange p-5">
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-brand-orange">
                  Aus der Praxis
                </span>
                <p className="mt-2 text-sm text-white/80 italic leading-relaxed">{hf.beispiel}</p>
              </div>
            )}
          </section>

          {/* Actions */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-brand-orange" />
              <h2 className="font-display text-sm uppercase tracking-wide text-white">
                Konkrete Maßnahmen
              </h2>
            </div>
            <div className="space-y-3">
              {hf.actions.map((a, i) => (
                <div key={a} className="flex items-start gap-3 bg-card border border-white/10 p-4">
                  <span className="font-display text-brand-orange text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-white/75 leading-relaxed">{a}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* RIGHT: sticky value + CTA */}
        <aside className="space-y-6">
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="bg-gradient-to-b from-brand-orange/10 to-transparent border border-brand-orange/30 p-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-brand-orange" />
                <span className="font-display text-xs uppercase tracking-wide text-white">
                  Der Werthebel
                </span>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">{hf.benchmark_fact}</p>
            </div>

            <ShareBar hf={hf} />

            <div className="bg-brand-orange p-6">
              <h3 className="font-display text-lg uppercase tracking-tight text-black leading-snug">
                Wo stehen Sie in diesem Feld?
              </h3>
              <p className="mt-2 text-sm text-black/70 leading-relaxed">
                Der Lagecheck zeigt es Ihnen in 5 Minuten – über alle 7 Felder.
              </p>
              <Button
                data-testid="hf-start-check"
                onClick={() => navigate("/")}
                className="mt-4 w-full h-12 rounded-none bg-black text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider text-sm transition-colors duration-300"
              >
                <Rocket className="mr-2 h-4 w-4" />
                Lagecheck starten
              </Button>
              <a href={ZOHO_BOOKING} target="_blank" rel="noopener noreferrer" data-testid="hf-book-btn">
                <Button
                  variant="ghost"
                  className="mt-2 w-full h-11 rounded-none text-black/70 hover:bg-black/10 hover:text-black uppercase tracking-wider text-xs"
                >
                  <CalendarCheck className="mr-2 h-4 w-4" />
                  Lagebesprechung buchen
                </Button>
              </a>
            </div>
          </div>
        </aside>
      </div>

      {/* Prev / Next */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-20 flex items-center justify-between border-t border-white/10 pt-8">
        {prevId ? (
          <Link to={`/handlungsfeld/${prevId}`} data-testid="hf-prev" className="group inline-flex items-center gap-2 text-white/60 hover:text-brand-orange transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Vorheriges Feld</span>
          </Link>
        ) : <span />}
        {nextId ? (
          <Link to={`/handlungsfeld/${nextId}`} data-testid="hf-next" className="group inline-flex items-center gap-2 text-white/60 hover:text-brand-orange transition-colors">
            <span className="text-sm">Nächstes Feld</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        ) : <span />}
      </section>
    </div>
  );
}
